import type { DailySharePayload } from '@/utils/dailyShare';
import { captureError } from '@/sentry';

type DailyShareCreateResponse = {
  id?: string;
};

type DailyShareFetchResponse = {
  date?: string;
  score?: number;
  name?: string | null;
};

export async function createDailyShare(payload: {
  date: string;
  score: number;
  name?: string | null;
  dailyId?: string | null;
}): Promise<{ id: string | null; error?: string }> {
  try {
    const response = await fetch('/api/daily-share', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const bodyText = await response.text();
      const error = new Error(`dailyShare.create failed (${response.status}): ${bodyText}`);
      captureError(error, { scope: 'dailyShare', action: 'create', status: response.status });
      console.warn('[dailyShare] create failed:', response.status, bodyText);
      return { id: null, error: `server_error_${response.status}` };
    }

    const data = await response.json() as DailyShareCreateResponse;
    if (typeof data.id !== 'string') {
      const error = new Error('dailyShare.create missing id');
      captureError(error, { scope: 'dailyShare', action: 'create' });
      console.warn('[dailyShare] create missing id');
      return { id: null, error: 'invalid_response' };
    }

    console.log('[dailyShare] create success', data.id);
    return { id: data.id };
  } catch (error) {
    captureError(error, { scope: 'dailyShare', action: 'create' });
    console.warn('[dailyShare] create error', error);
    return { id: null, error: 'network_error' };
  }
}

export async function fetchDailyShare(shareId: string): Promise<{ payload: DailySharePayload | null; error?: string }> {
  if (!shareId) {
    return { payload: null, error: 'missing_id' };
  }
  try {
    const response = await fetch(`/api/daily-share?s=${encodeURIComponent(shareId)}`);
    if (!response.ok) {
      const bodyText = await response.text();
      const error = new Error(`dailyShare.fetch failed (${response.status}): ${bodyText}`);
      captureError(error, { scope: 'dailyShare', action: 'fetch', status: response.status });
      console.warn('[dailyShare] fetch failed:', response.status, bodyText);
      return { payload: null, error: `server_error_${response.status}` };
    }
    const data = await response.json() as DailyShareFetchResponse;
    if (typeof data.date !== 'string' || typeof data.score !== 'number') {
      const error = new Error('dailyShare.fetch invalid payload');
      captureError(error, { scope: 'dailyShare', action: 'fetch' });
      console.warn('[dailyShare] fetch invalid payload');
      return { payload: null, error: 'invalid_response' };
    }
    console.log('[dailyShare] fetch success', shareId);
    return {
      payload: {
        date: data.date,
        score: data.score,
        name: data.name ?? null,
      },
    };
  } catch (error) {
    captureError(error, { scope: 'dailyShare', action: 'fetch' });
    console.warn('[dailyShare] fetch error', error);
    return { payload: null, error: 'network_error' };
  }
}
